import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquare, Clock, CheckCircle, XCircle, Send, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';

import { API_URL } from '../utils/api';

const AdminSupportPage = () => {
  const { user, token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Check if admin
  const isAdmin = user?.email === 'ebonnie_faith@hotmail.com' || user?.is_admin;

  useEffect(() => {
    if (isAdmin && token) {
      fetchTickets();
    }
  }, [isAdmin, token]);

  const fetchTickets = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/support/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data.tickets);
    } catch (err) {
      console.error('Failed to fetch tickets', err);
      toast.error('Failed to load support tickets');
    } finally {
      setLoading(false);
    }
  };

  const selectTicket = async (ticket) => {
    setSelectedTicket(ticket);
    try {
      const res = await axios.get(`${API_URL}/api/admin/support/ticket/${ticket.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages);
    } catch (err) {
      console.error('Failed to fetch messages', err);
      toast.error('Failed to load messages');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/support/ticket/${selectedTicket.id}/reply`,
        { ticket_id: selectedTicket.id, message: reply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply sent');
      setReply('');
      selectTicket(selectedTicket); // Refresh messages
      fetchTickets(); // Refresh ticket list
    } catch (err) {
      console.error('Failed to send reply', err);
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    try {
      await axios.put(
        `${API_URL}/api/admin/support/ticket/${selectedTicket.id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket closed');
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      console.error('Failed to close ticket', err);
      toast.error('Failed to close ticket');
    }
  };

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-900">Access Denied</h1>
          <p className="text-stone-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'replied': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-stone-100 text-stone-800';
      default: return 'bg-stone-100 text-stone-800';
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-stone-900 mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
          Customer Support
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Ticket List */}
          <div className="lg:col-span-1 bg-white rounded-lg border border-stone-200 overflow-hidden">
            <div className="p-4 border-b border-stone-200">
              <h2 className="font-semibold text-stone-900">Support Tickets</h2>
            </div>
            <div className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-stone-500">Loading...</div>
              ) : tickets.length === 0 ? (
                <div className="p-4 text-center text-stone-500">No tickets yet</div>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    onClick={() => selectTicket(ticket)}
                    className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                      selectedTicket?.id === ticket.id ? 'bg-teal-50 border-l-4 border-teal-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-stone-900">{ticket.name}</p>
                        <p className="text-sm text-stone-500">{ticket.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-xs text-stone-400 mt-2">
                      {new Date(ticket.updated_at).toLocaleDateString()}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Conversation */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-stone-200 overflow-hidden">
            {selectedTicket ? (
              <>
                {/* Ticket Header */}
                <div className="p-4 border-b border-stone-200 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setSelectedTicket(null)}
                      className="lg:hidden p-2 hover:bg-stone-100 rounded-full"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                      <h2 className="font-semibold text-stone-900">{selectedTicket.name}</h2>
                      <p className="text-sm text-stone-500">{selectedTicket.email}</p>
                    </div>
                  </div>
                  {selectedTicket.status !== 'closed' && (
                    <Button
                      onClick={closeTicket}
                      variant="outline"
                      size="sm"
                      className="text-stone-500"
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Close
                    </Button>
                  )}
                </div>

                {/* Messages */}
                <div className="p-4 h-[400px] overflow-y-auto space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'admin' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.sender_type === 'admin'
                            ? 'bg-gradient-to-r from-sky-500 to-teal-500 text-white'
                            : 'bg-stone-100 text-stone-900'
                        }`}
                      >
                        <p className="text-sm font-medium mb-1">
                          {msg.sender_type === 'admin' ? 'Customer Care' : msg.sender_name}
                        </p>
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-2 ${
                          msg.sender_type === 'admin' ? 'text-sky-200' : 'text-stone-400'
                        }`}>
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Input */}
                {selectedTicket.status !== 'closed' && (
                  <div className="p-4 border-t border-stone-200">
                    <div className="flex gap-2">
                      <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        placeholder="Type your reply..."
                        className="flex-1 min-h-[80px] resize-none"
                      />
                      <Button
                        onClick={sendReply}
                        disabled={sending || !reply.trim()}
                        className="bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 self-end"
                      >
                        {sending ? (
                          <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="h-full flex items-center justify-center text-stone-400">
                <div className="text-center">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Select a ticket to view the conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportPage;
